import {getChat, sendChat, type Comment} from "./api";
import type {ChatVisit} from "./chat-visit";

/** Chat has no timeline anchor and never creates a DAW marker. */
export class Discussion {
  readonly comments = document.createElement("div");
  private chat = document.createElement("section");
  private list = document.createElement("ol");
  private input = document.createElement("textarea");
  private send = document.createElement("button");
  private notice = document.createElement("p");
  private error = document.createElement("p");
  private newMessages = document.createElement("button");
  private lastId = "";
  private archiveExpanded = false;
  private messages: Comment[] = [];
  private form = document.createElement("form");
  private lastKey = "";
  private unread = 0;
  private open = false;
  private canWrite = false;
  private busy = false;
  private disposed = false;
  private polling = false;
  private timer: number;

  constructor(private root: HTMLElement, private deps: {session: string; token: () => string | undefined; host: boolean; visit: ChatVisit; chatRoot?: HTMLElement; onState: (open: boolean, canWrite: boolean, canComment: boolean) => void}) {
    root.classList.add("discussion");
    const commentsHeading = document.createElement("h2"); commentsHeading.textContent = "Comments";
    const chatHeading = document.createElement("h2"); chatHeading.textContent = "Chat";
    this.chat.className = "discussion__chat"; this.chat.setAttribute("aria-label", "Chat");
    this.comments.className = "discussion__comments";
    this.newMessages.type = "button"; this.newMessages.className = "discussion__new"; this.newMessages.hidden = true;
    this.newMessages.onclick = () => { this.list.scrollTop = this.list.scrollHeight; this.unread = 0; this.paint(); };
    this.list.onscroll = () => { if (this.atBottom() && this.unread) { this.unread = 0; this.paint(); } };
    this.notice.className = "control-room__muted";
    this.list.className = "discussion__messages";
    this.list.setAttribute("aria-label", "Chat messages");
    this.input.maxLength = 500; this.input.rows = 2; this.input.placeholder = "Write a message…"; this.input.setAttribute("aria-label", "Chat message");
    this.input.title = "Enter to send · Shift+Enter for a new line";
    this.input.addEventListener("keydown", e => {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing && e.keyCode !== 229 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        void this.submit();
      }
    });
    // Keep the keyboard open when tapping Send from the active composer.
    this.send.addEventListener("pointerdown", e => {
      if (document.activeElement === this.input) e.preventDefault();
    });
    this.send.type = "submit"; this.send.textContent = "Send"; this.send.className = "btn btn--primary";
    const form = this.form; form.className = "discussion__compose"; form.append(this.input,this.send);
    this.error.setAttribute("role","alert");
    form.onsubmit = e => { e.preventDefault(); void this.submit(); };
    const chatHeader = document.createElement("header"); chatHeader.className = "discussion__header";
    chatHeader.append(chatHeading);
    this.chat.append(chatHeader,this.notice,this.list,this.newMessages,form,this.error);
    const commentsSection = document.createElement("section"); commentsSection.className = "discussion__feedback";
    commentsSection.setAttribute("aria-label", "Comments"); commentsSection.append(commentsHeading,this.comments);
    root.append(commentsSection);
    if (deps.chatRoot) { deps.chatRoot.append(this.chat); root.classList.add("discussion--separate-chat"); }
    else root.append(this.chat);
    this.paint(); void this.refresh(); this.timer = window.setInterval(() => { void this.refresh(); }, 2000);
  }
  setState(open: boolean, canWrite: boolean) {
    this.open = open; this.canWrite = canWrite; this.paint();
  }
  private paint() {
    this.root.hidden = !this.deps.host && !this.open && !this.lastId;
    this.notice.hidden = this.open;
    this.notice.textContent = this.deps.host ? "Open communication to enable chat and comments." : "Communication is closed. You can keep listening.";
    this.newMessages.hidden = this.unread === 0;
    this.newMessages.textContent = `${this.unread} new ${this.unread === 1 ? "message" : "messages"}`;
    this.input.disabled = !this.open || !this.canWrite;
    this.send.disabled = !this.open || !this.canWrite || this.busy;
  }
  private async refresh() {
    const token = this.deps.token(); if (!token || this.polling || this.disposed) return;
    this.polling = true;
    try {
      const snapshot = await getChat(this.deps.session,token); if (this.disposed) return;
      if (!Array.isArray(snapshot.messages)) return;
      this.open = snapshot.open; this.canWrite = snapshot.canWrite;
      this.deps.onState(this.open,this.canWrite,snapshot.canComment ?? this.canWrite);
      this.renderMessages(snapshot.messages); this.paint();
    } catch { /* Keep drafts. The next poll recovers; sending still checks server permission. */ }
    finally { this.polling = false; }
  }
  private atBottom() { return this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight < 40; }
  private renderMessages(messages: Comment[]) {
    this.messages = messages;
    const groups = this.deps.visit.split(messages);
    const visible = this.archiveExpanded ? [...groups.archive, ...groups.current] : groups.current;
    const key = JSON.stringify([this.archiveExpanded,messages]); if (key === this.lastKey) return;
    const newest = messages.at(-1)?.id ?? "";
    const bottom = this.atBottom();
    const scrollTop = this.list.scrollTop;
    if (this.lastKey && newest !== this.lastId && !bottom) {
      const index = messages.findIndex(m => m.id === this.lastId);
      this.unread += index >= 0 ? messages.length - index - 1 : messages.length;
    }
    this.lastKey = key; this.lastId = newest;
    this.list.replaceChildren();
    if (groups.archive.length) {
      const row = document.createElement("li");
      const toggle = document.createElement("button"); toggle.type = "button";
      toggle.className = "discussion__archive-toggle";
      toggle.textContent = `${this.archiveExpanded ? "Hide" : "Show"} ${groups.archive.length} earlier ${groups.archive.length === 1 ? "message" : "messages"}`;
      toggle.setAttribute("aria-expanded", String(this.archiveExpanded));
      toggle.onclick = () => {
        const oldHeight = this.list.scrollHeight, oldTop = this.list.scrollTop;
        this.archiveExpanded = !this.archiveExpanded;
        this.lastKey = ""; this.renderMessages(this.messages);
        this.list.scrollTop = Math.max(0, oldTop + this.list.scrollHeight - oldHeight);
      };
      row.append(toggle); this.list.append(row);
    }
    for (const message of visible) {
      const row = document.createElement("li");
      const author = document.createElement("strong"); author.textContent = message.author;
      const time = document.createElement("time"); time.dateTime = new Date(message.ts).toISOString(); time.textContent = groups.archive.some(m => m.id === message.id) ? new Date(message.ts).toLocaleString([], {day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}) : new Date(message.ts).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
      const body = document.createElement("p"); body.textContent = message.text;
      row.append(author,time,body); this.list.append(row);
    }
    if (!visible.length) {
      const empty = document.createElement("li"); empty.className = "discussion__empty";
      empty.textContent = "No new messages yet.";
      this.list.append(empty);
    }
    if (bottom) { this.list.scrollTop = this.list.scrollHeight; this.unread = 0; }
    else this.list.scrollTop = scrollTop;
  }
  private async submit() {
    const token = this.deps.token(), text = this.input.value.trim();
    if (!token || !text || this.busy || !this.open || !this.canWrite) return;
    // Focus within the user gesture, never after awaiting a network request.
    this.input.focus({preventScroll: true});
    const draft = this.input.value;
    this.input.value = "";
    this.busy = true; this.error.textContent = ""; this.paint();
    try { await sendChat(this.deps.session,token,text); await this.refresh(); this.list.scrollTop = this.list.scrollHeight; this.unread = 0; }
    catch (e) {
      this.input.value = this.input.value ? `${draft}\n${this.input.value}` : draft;
      this.error.textContent = e instanceof Error ? e.message : "Message could not be sent. Please retry."; }
    finally { this.busy = false; this.paint(); }
  }
  destroy() { this.disposed = true; window.clearInterval(this.timer); }
}
